"use client";

import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { useTheme } from "next-themes";
import { AnimatePresence, motion } from "framer-motion";
import {
  History,
  Globe,
  Shield,
  Settings,
  PanelRight,
  Copy,
  SquareSplitVertical,
  ThumbsUp,
  ThumbsDown,
  GitCompareArrows,
  Plus,
  Mic,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Eye,
  Brain,
  Image as ImageIcon,
} from "lucide-react";
import {
  Ai21Icon,
  AnthropicIcon,
  DeepSeekIcon,
  GoogleIcon,
  MetaIcon,
  MistralIcon,
  MoonshotIcon,
  NewThisMonthIcon,
  NvidiaIcon,
  OpenAIIcon,
  ProviderIconDefs,
  QwenIcon,
  SakanaIcon,
  SpaceXaiIcon,
  ZaiIcon,
  type ProviderIconProps,
} from "@/components/ProviderIcons";
import DashboardAppPreview from "@/components/launchlemonade/DashboardAppPreview";
import WorkflowAppPreview from "@/components/launchlemonade/WorkflowAppPreview";
import TeamAppPreview from "@/components/launchlemonade/TeamAppPreview";
import {
  CanvasView,
  IntegrationsView,
  ModelsView,
  PrivacyView,
  ScheduleView,
  StoreView,
  TraceView,
  VoiceView,
} from "@/components/launchlemonade/FeatureViews";

/* The four app pages — used by the NON-tour mounts (/platform/teams,
   /consulting, HeroSection1) as a product-page rotation. */
const viewTabs = [
  { key: "workspace", label: "Assistants", caption: "" },
  { key: "chat", label: "Chat", caption: "" },
  { key: "workflow", label: "Schedules", caption: "" },
  { key: "team", label: "Team", caption: "" },
] as const;

type ViewTabKey = (typeof viewTabs)[number]["key"];

/* Tour mode (homepage hero): USPs only — Joe's brief. Plain chat, the team
   roster and the workflow LIST are out (everyone knows what chat is; app
   pages mean nothing to a stranger). Every frame is a product moment
   starring one differentiator. Order: what it DOES first (through the
   store), then the trust pair — agent trace and privacy — at the bottom
   of the rail. */
const tourTabs = [
  {
    key: "models",
    label: "Any model",
    caption: "Switch models mid-chat, or get a second opinion before you act.",
  },
  {
    key: "voice",
    label: "Voice",
    caption: "Talk it through out loud, hands-free.",
  },
  {
    key: "canvas",
    label: "Live dashboards",
    caption: "One ask becomes a working dashboard: KPIs, projections, posting heatmap.",
  },
  {
    key: "schedule",
    label: "Scheduled runs",
    caption: "Work that runs without you and lands in your inbox.",
  },
  {
    key: "tools",
    label: "Your tools",
    caption: "It acts in your tools: drafting, scheduling, filing.",
  },
  {
    key: "store",
    label: "Store",
    caption: "Publish yours. Free to list, you earn when it sells.",
  },
  {
    key: "trace",
    label: "Agent trace",
    caption: "Watch it work. Every step and tool call logged.",
  },
  {
    key: "privacy",
    label: "Privacy",
    caption: "With PII masking on, personal details are masked before they reach a model.",
  },
] as const;

type AnyViewKey = ViewTabKey | (typeof tourTabs)[number]["key"];

/* Tour-mode annotations: one bold gold pill per view, anchored on the detail
   that sells it. Percent positions are tuned against the md aspect ratio and
   hidden below md, where the mocks reflow. */
const viewCallouts: Partial<
  Record<AnyViewKey, { text: string; style: React.CSSProperties }[]>
> = {
  canvas: [
    { text: "You asked once. It built this", style: { left: "13%", bottom: "12%" } },
  ],
  trace: [
    { text: "It shows its work", style: { right: "10%", top: "34%" } },
  ],
  models: [
    { text: "From Claude to Gemini, thread kept", style: { right: "8%", top: "52%" } },
  ],
  voice: [
    { text: "Voice mode, no typing", style: { left: "12%", top: "22%" } },
  ],
  privacy: [
    { text: "Masked before the model sees it", style: { right: "8%", top: "48%" } },
  ],
  schedule: [
    { text: "Ran at 07:02 without you", style: { right: "8%", top: "20%" } },
  ],
  tools: [
    { text: "It acts, not just reads", style: { right: "8%", top: "56%" } },
  ],
  store: [
    { text: "You earn when it sells", style: { right: "8%", top: "24%" } },
  ],
};

/** How long each preview stays on screen before auto-advancing. */
const VIEW_ROTATE_MS = 6000;

/** Same crossfade used by the hero's logo stack swap, above the eyebrow text. */
const EASE_OUT = [0.22, 1, 0.36, 1] as const;

const messageActions = [
  Copy,
  SquareSplitVertical,
  ThumbsUp,
  ThumbsDown,
  GitCompareArrows,
];

/** Vertical bounds of the floating model picker, as a fraction of the preview. */
const pickerTop = 0.34;
const pickerBottom = 0.86;

type ModelProvider = {
  label: string;
  count: number;
  Icon: (props: ProviderIconProps) => React.ReactElement;
};

const modelProviders: ModelProvider[] = [
  { label: "New this month", count: 13, Icon: NewThisMonthIcon },
  { label: "Anthropic", count: 18, Icon: AnthropicIcon },
  { label: "OpenAI", count: 46, Icon: OpenAIIcon },
  { label: "Google", count: 10, Icon: GoogleIcon },
  { label: "DeepSeek", count: 3, Icon: DeepSeekIcon },
  { label: "Z.ai", count: 6, Icon: ZaiIcon },
  { label: "Moonshot / Kimi", count: 7, Icon: MoonshotIcon },
  { label: "Qwen", count: 7, Icon: QwenIcon },
  { label: "NVIDIA", count: 1, Icon: NvidiaIcon },
  { label: "Mistral", count: 5, Icon: MistralIcon },
  { label: "Meta", count: 1, Icon: MetaIcon },
  { label: "Ai21", count: 1, Icon: Ai21Icon },
];

type ProviderModel = {
  name: string;
  context: string;
  /** Shows the Eye (vision) badge. Defaults to true. */
  vision?: boolean;
  /** Shows the Brain (reasoning) badge. Defaults to true. */
  reasoning?: boolean;
  /** Shows the image-generation badge. Defaults to false. */
  imageGen?: boolean;
  /** Row icon override, for mixed-provider lists like "New this month". */
  Icon?: (props: ProviderIconProps) => React.ReactElement;
};

const modelsByProvider: Record<string, ProviderModel[]> = {
  "New this month": [
    {
      name: "DeepSeek: DeepSeek V4 Flash Vision Exp",
      context: "1M",
      Icon: DeepSeekIcon,
    },
    { name: "Google: Gemini 3.7 Flash", context: "1M", Icon: GoogleIcon },
    { name: "Qwen: Qwen3.8 27B", context: "1M", Icon: QwenIcon },
    {
      name: "DeepSeek: DeepSeek V4 Pro 0813",
      context: "1M",
      vision: false,
      Icon: DeepSeekIcon,
    },
    { name: "Meta: Muse Spark 1.2", context: "1M", Icon: MetaIcon },
    {
      name: "Qwen: Qwen3.8 2.4T A95B",
      context: "1M",
      vision: false,
      Icon: QwenIcon,
    },
    { name: "Qwen: Qwen3.8 Max", context: "1M", Icon: QwenIcon },
    { name: "Sakana: Sakana Namazu", context: "262K", Icon: SakanaIcon },
    { name: "SpaceXAI: Grok 4.6", context: "500K", Icon: SpaceXaiIcon },
    { name: "Z.ai: GLM 5.3", context: "1M", vision: false, Icon: ZaiIcon },
    { name: "Z.ai: GLM Latest", context: "1M", vision: false, Icon: ZaiIcon },
  ],
  Anthropic: [
    { name: "Claude Haiku 4.5", context: "200K" },
    { name: "Claude Opus 4.5", context: "200K" },
    { name: "Claude Opus 4.6", context: "1M" },
    { name: "Claude Sonnet 4.5", context: "1M" },
    { name: "Claude Sonnet 4.6", context: "1M" },
    { name: "Claude Haiku Latest", context: "200K" },
    { name: "Claude Sonnet Latest", context: "1M" },
    { name: "Claude Fable 5", context: "1M" },
    { name: "Claude Fable Latest", context: "1M" },
    { name: "Claude Opus 4", context: "200K" },
    { name: "Claude Opus 4.1", context: "200K" },
    { name: "Claude Opus 4.7", context: "1M" },
    { name: "Claude Opus 4.7 (Fast)", context: "1M" },
    { name: "Claude Opus 4.8 (Fast)", context: "1M" },
    { name: "Claude Opus Latest", context: "1M" },
    { name: "Claude Sonnet 4", context: "1M" },
    { name: "Claude Sonnet 5", context: "1M" },
    { name: "Claude Opus 5 (Fast)", context: "1M" },
  ],
  OpenAI: [
    { name: "GPT-5 Pro", context: "400K" },
    { name: "GPT-5.1", context: "400K" },
    { name: "GPT-5.1-Codex", context: "400K" },
    { name: "GPT-5.1-Codex-Max", context: "400K" },
    { name: "GPT-5.2", context: "400K" },
    { name: "GPT-5.2 Pro", context: "400K" },
    { name: "GPT-5.2-Codex", context: "400K" },
    { name: "GPT-5.3-Codex", context: "400K" },
    { name: "GPT Latest", context: "1.1M" },
    { name: "GPT Mini Latest", context: "400K" },
    { name: "GPT Audio", context: "128K", vision: false, reasoning: false },
    {
      name: "GPT Audio Mini",
      context: "128K",
      vision: false,
      reasoning: false,
    },
    { name: "GPT Chat Latest", context: "400K", reasoning: false },
    {
      name: "GPT-3.5 Turbo (older v0613)",
      context: "4K",
      vision: false,
      reasoning: false,
    },
    {
      name: "GPT-3.5 Turbo 16k",
      context: "16K",
      vision: false,
      reasoning: false,
    },
    {
      name: "GPT-3.5 Turbo Instruct",
      context: "4K",
      vision: false,
      reasoning: false,
    },
    { name: "GPT-4", context: "8K", vision: false, reasoning: false },
    { name: "GPT-4 Turbo", context: "128K", reasoning: false },
    {
      name: "GPT-4 Turbo Preview",
      context: "128K",
      vision: false,
      reasoning: false,
    },
    { name: "GPT-4.1", context: "1M", reasoning: false },
    { name: "GPT-4o", context: "128K", reasoning: false },
    { name: "GPT-4o (2024-05-13)", context: "128K", reasoning: false },
    { name: "GPT-4o (2024-08-06)", context: "128K", reasoning: false },
    { name: "GPT-4o (2024-11-20)", context: "128K", reasoning: false },
    { name: "GPT-5", context: "400K" },
    { name: "GPT-5 Image", context: "400K" },
    { name: "GPT-5 Image Mini", context: "400K" },
    { name: "GPT-5.2 Chat", context: "128K", reasoning: false },
    { name: "GPT-5.4", context: "1.1M", imageGen: true },
    { name: "GPT-5.4 Image 2", context: "272K", imageGen: true },
    { name: "GPT-5.4 Mini", context: "400K" },
    { name: "GPT-5.4 Pro", context: "1.1M" },
    { name: "GPT-5.5", context: "1.1M" },
    { name: "GPT-5.5 Pro", context: "1.1M" },
    { name: "GPT-5.6 Sol", context: "1.1M" },
    { name: "GPT-5.6 Sol Pro", context: "1.1M" },
    { name: "GPT-5.6 Terra", context: "1.1M" },
    { name: "o1", context: "200K" },
    { name: "o1-pro", context: "200K" },
    { name: "o3", context: "200K" },
    { name: "o3 Mini", context: "200K", vision: false },
    { name: "o3 Mini High", context: "200K", vision: false },
    { name: "o3 Pro", context: "200K" },
    { name: "o4 Mini", context: "200K" },
    { name: "o4 Mini High", context: "200K" },
    { name: "GPT-5.6 Luna Pro", context: "1.1M" },
  ],
  Google: [
    { name: "Gemini 3 Pro", context: "2M" },
    { name: "Gemini 3 Flash", context: "1M" },
    { name: "Gemini 2.5 Pro", context: "2M" },
    { name: "Gemini 2.5 Flash", context: "1M" },
    { name: "Gemini 2.5 Flash-Lite", context: "1M" },
  ],
  DeepSeek: [
    { name: "DeepSeek V3.2", context: "128K" },
    { name: "DeepSeek R1", context: "128K" },
    { name: "DeepSeek Coder V3", context: "128K" },
  ],
  "Z.ai": [
    { name: "GLM-4.6", context: "200K" },
    { name: "GLM-4.5", context: "128K" },
    { name: "GLM-4.5-Air", context: "128K" },
    { name: "GLM-4.5V", context: "64K" },
    { name: "GLM-4-Plus", context: "128K" },
    { name: "GLM-4-Flash", context: "128K" },
  ],
  "Moonshot / Kimi": [
    { name: "Kimi K2", context: "256K" },
    { name: "Kimi K2 Turbo", context: "256K" },
    { name: "Kimi K1.5", context: "128K" },
    { name: "Moonshot v1 128K", context: "128K" },
    { name: "Moonshot v1 32K", context: "32K" },
    { name: "Moonshot v1 8K", context: "8K" },
    { name: "Kimi Latest", context: "256K" },
  ],
  Qwen: [
    { name: "Qwen3-Max", context: "256K" },
    { name: "Qwen3-235B", context: "128K" },
    { name: "Qwen3-Coder", context: "256K" },
    { name: "Qwen2.5-VL", context: "128K" },
    { name: "Qwen2.5-72B", context: "128K" },
    { name: "Qwen2.5-Coder", context: "128K" },
    { name: "QwQ-32B", context: "32K" },
  ],
  NVIDIA: [{ name: "Nemotron-4 340B", context: "128K" }],
  Mistral: [
    { name: "Mistral Large 3", context: "256K" },
    { name: "Mistral Medium 3", context: "128K" },
    { name: "Mistral Small 3", context: "128K" },
    { name: "Codestral 2", context: "256K" },
    { name: "Pixtral Large", context: "128K" },
  ],
  Meta: [{ name: "Llama 4 Maverick", context: "1M" }],
  Ai21: [{ name: "Jamba 1.6 Large", context: "256K" }],
};

const providerIconByLabel: Record<string, ModelProvider["Icon"]> =
  Object.fromEntries(modelProviders.map((p) => [p.label, p.Icon]));

export default function ChatAppPreview({
  className = "",
  showFloatingPanels = true,
  interactivePicker = false,
  glass = false,
  tour = false,
}: {
  className?: string;
  showFloatingPanels?: boolean;
  /** Makes the "Claude Opus 5" pill open the model picker as an anchored dropdown on click. */
  interactivePicker?: boolean;
  /** Annotated tour mode (homepage hero): labelled view tabs with a rotation
      progress bar, a crossfading caption, and callout chips over each view. */
  tour?: boolean;
  /** Renders the outer window frame as frosted glass — a light or dark
      recipe picked up automatically from the `.light`/`.dark` theme class
      via CSS variables. Needs a saturated shape behind the mockup (e.g. a
      glow blob) for the blur to diffuse against. */
  glass?: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = mounted && resolvedTheme === "dark";
  const glassActive = glass;

  const tabs: readonly { key: AnyViewKey; label: string; caption: string }[] =
    tour ? tourTabs : viewTabs;
  const [activeView, setActiveView] = useState<AnyViewKey>(tabs[0].key);
  /* Bumped on manual tab clicks so the auto-advance timer restarts — a click
     buys the reader a full VIEW_ROTATE_MS on their chosen view. */
  const [rotationEpoch, setRotationEpoch] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setActiveView((current) => {
        const nextIndex =
          (tabs.findIndex((t) => t.key === current) + 1) % tabs.length;
        return tabs[nextIndex].key;
      });
    }, VIEW_ROTATE_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotationEpoch, tour]);

  const selectView = (key: AnyViewKey) => {
    setActiveView(key);
    setRotationEpoch((n) => n + 1);
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [message, setMessage] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<{
    name: string;
    Icon: (props: ProviderIconProps) => React.ReactElement;
  }>({ name: "Claude Opus 5", Icon: AnthropicIcon });
  const [flyoutPos, setFlyoutPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!interactivePicker || !pickerOpen) return;
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (pickerRef.current?.contains(target)) return;
      if (flyoutRef.current?.contains(target)) return;
      if (pillRef.current?.contains(target)) return;
      setPickerOpen(false);
      setHoveredLabel(null);
      setFlyoutPos(null);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [interactivePicker, pickerOpen]);

  const openFlyout = (label: string) => (e: MouseEvent<HTMLDivElement>) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setHoveredLabel(label);
    const wrapperEl = wrapperRef.current;
    if (!wrapperEl) return;
    const rowRect = e.currentTarget.getBoundingClientRect();
    const wrapperRect = wrapperEl.getBoundingClientRect();
    const rowTop = rowRect.top - wrapperRect.top - 10;
    setFlyoutPos({
      top: rowTop,
      left: rowRect.right - wrapperRect.left + 10,
      width: wrapperRect.width * 0.27,
      // Stop roughly where the picker itself ends (see `pickerBottom`).
      maxHeight: Math.max(200, pickerBottom * wrapperRect.height - rowTop),
    });
  };

  const highlightOnly = (label: string) => () => setHoveredLabel(label);

  const scheduleCloseFlyout = () => {
    closeTimer.current = setTimeout(() => {
      setFlyoutPos(null);
      setHoveredLabel(null);
    }, 150);
  };

  const clearHighlight = () => setHoveredLabel(null);

  const selectModel =
    (name: string, Icon: (props: ProviderIconProps) => React.ReactElement) =>
    () => {
      if (!interactivePicker) return;
      setSelectedModel({ name, Icon });
      setPickerOpen(false);
      setHoveredLabel(null);
      setFlyoutPos(null);
    };

  const cancelCloseFlyout = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const renderPickerList = () => (
    <>
      <div className="px-4 pt-3 pb-1 shrink-0">
        <p className="text-[11px]" style={{ color: c.subLabel }}>
          Browse
        </p>
      </div>
      <div
        className="flex-1 overflow-y-auto px-2 pb-2"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: `${c.chevron} transparent`,
        }}
      >
        {modelProviders.map(({ label, count, Icon }) => {
          const models = modelsByProvider[label];
          return (
            <div
              key={label}
              className="flex items-center gap-3 rounded-lg px-2 py-2"
              style={
                hoveredLabel === label
                  ? {
                      background: isDark
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(0,0,0,0.045)",
                    }
                  : undefined
              }
              onMouseEnter={models ? openFlyout(label) : highlightOnly(label)}
              onMouseLeave={models ? scheduleCloseFlyout : clearHighlight}
            >
              <span className="w-5 h-5 flex items-center justify-center shrink-0">
                <Icon size={19} isDark={isDark} />
              </span>
              <span
                className="text-[14px] flex-1 truncate"
                style={{ color: c.messageText }}
              >
                {label}
              </span>
              <span className="text-[12px]" style={{ color: c.subLabel }}>
                {count}
              </span>
              <ChevronRight size={14} style={{ color: c.chevron }} />
            </div>
          );
        })}
      </div>
    </>
  );

  const resizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleMessageChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    resizeTextarea();
  };

  const handleMessageKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      setMessage("");
      requestAnimationFrame(resizeTextarea);
    }
  };

  const c = isDark
    ? {
        bg: "#0a0a0a",
        border: "rgba(255,255,255,0.08)",
        shadow: "0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)",
        icon: "rgba(255,255,255,0.55)",
        avatarBg: "rgba(255,255,255,0.08)",
        chatLabel: "rgba(255,255,255,0.6)",
        subLabel: "rgba(255,255,255,0.38)",
        shieldBg: "#3a2712",
        shieldIcon: "#e8a545",
        pillBorder: "rgba(255,255,255,0.12)",
        pillText: "rgba(255,255,255,0.45)",
        messageText: "rgba(255,255,255,0.9)",
        actionIcon: "rgba(255,255,255,0.4)",
        composerBg: "#161616",
        composerShadow:
          "0 1px 2px rgba(0,0,0,0.3), 0 24px 50px -28px rgba(0,0,0,0.6)",
        placeholder: "rgba(255,255,255,0.32)",
        plusBorder: "rgba(255,255,255,0.16)",
        plusIcon: "rgba(255,255,255,0.6)",
        modelBorder: "rgba(255,255,255,0.14)",
        modelText: "rgba(255,255,255,0.65)",
        chevron: "rgba(255,255,255,0.4)",
        micBg: "rgba(255,255,255,0.08)",
        micIcon: "rgba(255,255,255,0.55)",
        sendBg: "rgba(255,255,255,0.14)",
        sendIcon: "rgba(255,255,255,0.75)",
        floatBg: "#141414",
        floatBorder: "rgba(255,255,255,0.08)",
        floatShadow:
          "0 20px 60px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
        chromeBg: "#0d0d0e",
        chromeBorder: "rgba(255,255,255,0.08)",
        chromeDot: "rgba(255,255,255,0.16)",
        glassBg: "rgba(13,13,14,0.55)",
        glassBorder: "rgba(255,255,255,0.1)",
      }
    : {
        bg: "#e7e5df",
        border: "rgba(0,0,0,0.06)",
        shadow: "0 30px 80px rgba(0,0,0,0.16), 0 0 0 1px rgba(0,0,0,0.02)",
        icon: "rgba(0,0,0,0.55)",
        avatarBg: "rgba(0,0,0,0.06)",
        chatLabel: "rgba(0,0,0,0.55)",
        subLabel: "rgba(0,0,0,0.4)",
        shieldBg: "rgba(224,167,48,0.16)",
        shieldIcon: "#c9820a",
        pillBorder: "rgba(0,0,0,0.08)",
        pillText: "rgba(0,0,0,0.42)",
        messageText: "rgba(0,0,0,0.82)",
        actionIcon: "rgba(0,0,0,0.38)",
        composerBg: "#fffdf9",
        composerShadow:
          "0 1px 2px rgba(0,0,0,0.04), 0 24px 50px -28px rgba(0,0,0,0.3)",
        placeholder: "rgba(0,0,0,0.35)",
        plusBorder: "rgba(0,0,0,0.12)",
        plusIcon: "rgba(0,0,0,0.55)",
        modelBorder: "rgba(0,0,0,0.12)",
        modelText: "rgba(0,0,0,0.6)",
        chevron: "rgba(0,0,0,0.45)",
        micBg: "rgba(0,0,0,0.05)",
        micIcon: "rgba(0,0,0,0.5)",
        sendBg: "rgba(0,0,0,0.08)",
        sendIcon: "rgba(0,0,0,0.45)",
        floatBg: "#ffffff",
        floatBorder: "rgba(0,0,0,0.06)",
        floatShadow:
          "0 20px 60px -20px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.02)",
        chromeBg: "#f1efe9",
        chromeBorder: "rgba(0,0,0,0.06)",
        chromeDot: "rgba(0,0,0,0.14)",
        glassBg: "rgba(255,255,255,0.55)",
        glassBorder: "rgba(255,255,255,0.7)",
      };

  const activeViewContent =
    activeView === "workspace" ? (
      <DashboardAppPreview glass={glassActive} />
    ) : activeView === "workflow" ? (
      <WorkflowAppPreview glass={glassActive} />
    ) : activeView === "team" ? (
      <TeamAppPreview glass={glassActive} />
    ) : activeView === "canvas" ? (
      <CanvasView glass={glassActive} />
    ) : activeView === "trace" ? (
      <TraceView glass={glassActive} />
    ) : activeView === "models" ? (
      <ModelsView glass={glassActive} />
    ) : activeView === "voice" ? (
      <VoiceView glass={glassActive} />
    ) : activeView === "privacy" ? (
      <PrivacyView glass={glassActive} />
    ) : activeView === "schedule" ? (
      <ScheduleView glass={glassActive} />
    ) : activeView === "tools" ? (
      <IntegrationsView glass={glassActive} />
    ) : activeView === "store" ? (
      <StoreView glass={glassActive} />
    ) : (
      <div
        className={`flex flex-col aspect-[3/4] md:aspect-[2980/1894] px-6 md:px-10 pt-8 md:pt-10 pb-6 md:pb-8 ${
          glassActive ? "hero-glass-surface" : ""
        }`}
        style={
          glassActive
            ? undefined
            : {
                background: c.glassBg,
                borderTop: `1px solid ${c.glassBorder}`,
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
              }
        }
      >
        {/* Top bar */}
        <div className="flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <span
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ color: c.icon }}
            >
              <History size={16} />
            </span>
            <div className="leading-tight">
              <p className="text-[13px]" style={{ color: c.chatLabel }}>
                AI Implementation Consulting Assistant
              </p>
              <p className="text-[13px] italic" style={{ color: c.subLabel }}>
                New conversation
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <span className="relative group">
              <span
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ color: c.icon }}
              >
                <Globe size={16} />
              </span>
              <span
                className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 -translate-x-1/2 scale-95 whitespace-nowrap px-3 py-1.5 text-[13px] font-medium text-white opacity-0 transition-all duration-150 group-hover:scale-100 group-hover:opacity-100"
                style={{
                  background: "#1c1c1c",
                  borderRadius: "4px 12px 12px 12px",
                }}
              >
                Search the internet
              </span>
            </span>
            <span
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: c.shieldBg, color: c.shieldIcon }}
            >
              <Shield size={16} />
            </span>
            <span className="relative group">
              <span
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ color: c.icon }}
              >
                <Settings size={16} />
              </span>
              <span
                className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 -translate-x-1/2 scale-95 whitespace-nowrap px-3 py-1.5 text-[13px] font-medium text-white opacity-0 transition-all duration-150 group-hover:scale-100 group-hover:opacity-100"
                style={{
                  background: "#1c1c1c",
                  borderRadius: "4px 12px 12px 12px",
                }}
              >
                Agent settings
              </span>
            </span>
            <span
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ color: c.icon }}
            >
              <PanelRight size={16} />
            </span>
          </div>
        </div>

        {/* Remembers pill */}
        <div className="flex justify-center mt-6 md:mt-8 shrink-0">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px]"
            style={{ color: c.pillText, border: `1px solid ${c.pillBorder}` }}
          >
            <History size={12} />
            Chat remembers your past conversations
          </span>
        </div>

        {/* Assistant message */}
        <div className="mt-6 md:mt-8">
          <div
            className={`max-w-2xl mx-auto md:mx-0 ${
              isDark ? "px-1" : "rounded-[24px] px-5 py-4 md:px-6 md:py-5"
            }`}
            style={
              isDark
                ? undefined
                : {
                    background: "#ffffff",
                    boxShadow:
                      "0 1px 2px rgba(0,0,0,0.04), 0 16px 40px -24px rgba(0,0,0,0.25)",
                  }
            }
          >
            <p
              className="text-[14px] md:text-[15px] leading-relaxed"
              style={{ color: c.messageText }}
            >
              Hi, I&apos;m your AI Implementation Consulting Assistant. Ask me
              anything, paste something to work on, or switch models any time.
              What&apos;s on your mind?
            </p>
          </div>
          <div className="flex items-center gap-1 mt-3 pl-1">
            {messageActions.map((Icon, i) => (
              <span
                key={i}
                className="w-7 h-7 rounded-md flex items-center justify-center"
                style={{ color: c.actionIcon }}
              >
                <Icon size={14} />
              </span>
            ))}
          </div>
        </div>

        <div className="flex-1" />

        {/* Composer */}
        <div
          className="rounded-[26px] px-5 pt-4 pb-3.5 shrink-0"
          style={{
            background: c.composerBg,
            boxShadow: c.composerShadow,
          }}
        >
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleMessageChange}
            onKeyDown={handleMessageKeyDown}
            placeholder="Message..."
            rows={1}
            aria-hidden={false}
            aria-label="Message"
            className="block w-full resize-none bg-transparent text-[14px] mb-4 outline-none placeholder:text-[var(--composer-placeholder)]"
            style={
              {
                color: c.messageText,
                userSelect: "text",
                "--composer-placeholder": c.placeholder,
              } as React.CSSProperties
            }
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{
                  border: `1px solid ${c.plusBorder}`,
                  color: c.plusIcon,
                }}
              >
                <Plus size={15} />
              </span>
              <div className="relative">
                <button
                  ref={pillRef}
                  type="button"
                  disabled={!interactivePicker}
                  onClick={
                    interactivePicker
                      ? () => setPickerOpen((open) => !open)
                      : undefined
                  }
                  aria-expanded={interactivePicker ? pickerOpen : undefined}
                  aria-haspopup={interactivePicker ? "true" : undefined}
                  aria-hidden={interactivePicker ? false : undefined}
                  aria-label={interactivePicker ? "Choose model" : undefined}
                  className={`inline-flex items-center gap-1.5 rounded-full pl-3 pr-3 py-1.5 text-[13px] ${
                    interactivePicker ? "cursor-pointer" : "cursor-default"
                  }`}
                  style={{
                    border: `1px solid ${c.modelBorder}`,
                    color: c.modelText,
                  }}
                >
                  <selectedModel.Icon size={13} isDark={isDark} />
                  {selectedModel.name}
                  <ChevronDown
                    size={13}
                    style={{
                      color: c.chevron,
                      transform:
                        interactivePicker && pickerOpen
                          ? "rotate(180deg)"
                          : undefined,
                      transition: "transform 150ms",
                    }}
                  />
                </button>

                {/* Anchored dropdown: opens on click of the model pill */}
                {interactivePicker && pickerOpen && (
                  <div
                    ref={pickerRef}
                    aria-hidden={false}
                    className="absolute bottom-full left-0 mb-2 z-20 rounded-xl md:rounded-2xl border overflow-hidden flex flex-col"
                    style={{
                      width: "min(300px, 80vw)",
                      maxHeight: "min(360px, 60vh)",
                      background: c.floatBg,
                      borderColor: c.floatBorder,
                      boxShadow: c.floatShadow,
                    }}
                  >
                    {renderPickerList()}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: c.micBg, color: c.micIcon }}
              >
                <Mic size={15} />
              </span>
              <span
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: c.sendBg, color: c.sendIcon }}
              >
                <ArrowUp size={16} />
              </span>
            </div>
          </div>
        </div>
      </div>
    );

  const floatingPanels = (
    <>
      {/* Floating: model picker */}
      {showFloatingPanels && (
        <div
          className="absolute rounded-xl md:rounded-2xl border overflow-hidden hidden xl:flex xl:flex-col"
          style={{
            left: "-6%",
            top: `${pickerTop * 100}%`,
            width: "34%",
            height: `${(pickerBottom - pickerTop) * 100}%`,
            background: c.floatBg,
            borderColor: c.floatBorder,
            boxShadow: c.floatShadow,
          }}
        >
          {renderPickerList()}
        </div>
      )}

      {/* Flyout: model list for the hovered provider */}
      {(showFloatingPanels || (interactivePicker && pickerOpen)) &&
        flyoutPos &&
        hoveredLabel &&
        modelsByProvider[hoveredLabel] && (
          <div
            ref={flyoutRef}
            aria-hidden={interactivePicker ? false : undefined}
            className="absolute z-20 rounded-xl border overflow-hidden flex flex-col"
            style={{
              top: flyoutPos.top,
              left: flyoutPos.left,
              width: flyoutPos.width,
              maxHeight: flyoutPos.maxHeight,
              background: c.floatBg,
              borderColor: c.floatBorder,
              boxShadow: c.floatShadow,
            }}
            onMouseEnter={cancelCloseFlyout}
            onMouseLeave={scheduleCloseFlyout}
          >
            <div
              className="flex items-center gap-2.5 px-4 py-3 shrink-0"
              style={{ borderBottom: `1px solid ${c.floatBorder}` }}
            >
              {(() => {
                const FlyoutIcon = providerIconByLabel[hoveredLabel];
                return FlyoutIcon ? (
                  <FlyoutIcon size={17} isDark={isDark} />
                ) : null;
              })()}
              <span className="text-[14px]" style={{ color: c.messageText }}>
                {hoveredLabel}
              </span>
            </div>
            <div
              className="flex-1 overflow-y-auto px-2 py-2"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: `${c.chevron} transparent`,
              }}
            >
              {modelsByProvider[hoveredLabel].map(
                ({
                  name,
                  context,
                  vision = true,
                  reasoning = true,
                  imageGen = false,
                  Icon: ModelIcon,
                }) => {
                  const RowIcon =
                    ModelIcon ?? providerIconByLabel[hoveredLabel];
                  return (
                    <div
                      key={name}
                      role={interactivePicker ? "button" : undefined}
                      onClick={RowIcon ? selectModel(name, RowIcon) : undefined}
                      className={`flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 hover:bg-[var(--row-hover)] ${
                        interactivePicker ? "cursor-pointer" : ""
                      }`}
                      style={
                        {
                          "--row-hover": isDark
                            ? "rgba(255,255,255,0.06)"
                            : "rgba(0,0,0,0.045)",
                        } as React.CSSProperties
                      }
                    >
                      <span className="flex items-center gap-2.5 min-w-0">
                        <span className="shrink-0 flex items-center">
                          {RowIcon ? (
                            <RowIcon size={15} isDark={isDark} />
                          ) : null}
                        </span>
                        <span
                          className="text-[13px] truncate"
                          style={{ color: c.messageText }}
                        >
                          {name}
                        </span>
                      </span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {reasoning && (
                          <Brain size={13} style={{ color: "#a78bfa" }} />
                        )}
                        {vision && (
                          <Eye size={13} style={{ color: "#4285f4" }} />
                        )}
                        {imageGen && (
                          <ImageIcon size={13} style={{ color: "#f472b6" }} />
                        )}
                        <span
                          className="text-[12px] w-8 text-right"
                          style={{ color: c.subLabel }}
                        >
                          {context}
                        </span>
                      </span>
                    </div>
                  );
                },
              )}
            </div>
          </div>
        )}

      {/* Floating: privacy protection panel */}
      {showFloatingPanels && (
        <div
          className="absolute rounded-xl md:rounded-2xl border overflow-hidden hidden xl:block"
          style={{
            right: "-4%",
            bottom: "-3%",
            width: "38%",
            background: c.floatBg,
            borderColor: c.floatBorder,
            boxShadow: c.floatShadow,
          }}
        >
          <div className="px-4 py-3.5 md:px-5 md:py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <Shield size={19} style={{ color: "#e0a730" }} />
                <span
                  className="text-[15px] font-medium"
                  style={{ color: c.messageText }}
                >
                  Privacy protection
                </span>
              </div>
              <span
                className="relative inline-flex items-center shrink-0 rounded-full"
                style={{ width: 36, height: 19, background: "#e0a730" }}
              >
                <span
                  className="absolute rounded-full"
                  style={{
                    width: 15,
                    height: 15,
                    right: 2,
                    background: "#161616",
                  }}
                />
              </span>
            </div>
            <p
              className="text-[13px] leading-[1.45] mt-3"
              style={{ color: c.subLabel }}
            >
              Automatically detect and mask personal information (emails, phone
              numbers, credit cards, etc.) before sending to the LLM. Users can
              also manually highlight text to redact it.
            </p>
            <p
              className="text-[12px] leading-[1.45] mt-2.5"
              style={{ color: c.placeholder }}
            >
              Detection is best-effort. Complex or unusual formats may not be
              caught. A privacy instruction is added to the assistant&apos;s
              system prompt when this is on, which may affect response style. Do
              not rely on this as your sole data-protection measure.
            </p>
            <p
              className="text-[12px] leading-[1.45] mt-2.5"
              style={{ color: "#c2603d" }}
            >
              When disabled, messages are sent as-is without any privacy
              filtering.
            </p>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div
      ref={wrapperRef}
      className={`relative select-none text-left ${className} ${
        tour ? "lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:items-center lg:gap-10" : ""
      }`}
      aria-hidden="true"
    >
      <ProviderIconDefs />

      {/* Tour rail (lg+): the USP labels live BESIDE the frame — the
          highlight text sits outside the mock, and the hero gets shorter. */}
      {tour && (
        <div className="relative z-[2] hidden lg:flex lg:flex-col lg:gap-1">
          {tabs.map((t) => {
            const active = t.key === activeView;
            return (
              <button
                key={t.key}
                type="button"
                tabIndex={-1}
                onClick={() => selectView(t.key)}
                className="relative py-2 pl-5 text-left"
              >
                <span
                  className="absolute bottom-1.5 left-0 top-1.5 w-[2px] rounded-full"
                  style={{
                    background: active
                      ? "color-mix(in srgb, var(--primary) 25%, transparent)"
                      : "color-mix(in srgb, var(--foreground) 10%, transparent)",
                  }}
                />
                {active && (
                  <motion.span
                    key={`${activeView}-${rotationEpoch}-rail`}
                    className="absolute bottom-1.5 left-0 top-1.5 w-[2px] origin-top rounded-full"
                    style={{ background: "var(--primary)" }}
                    initial={{ scaleY: 0 }}
                    animate={{ scaleY: 1 }}
                    transition={{ duration: VIEW_ROTATE_MS / 1000, ease: "linear" }}
                  />
                )}
                <span
                  className="text-[14px] font-medium transition-colors duration-300"
                  style={{ color: active ? "var(--primary)" : c.pillText }}
                >
                  {t.label}
                </span>
                <AnimatePresence initial={false}>
                  {active && (
                    <motion.span
                      className="block overflow-hidden pr-1 text-[13.5px] normal-case leading-snug tracking-normal text-on-dark/65"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{
                        height: "auto",
                        opacity: 1,
                        transition: { duration: 0.35, ease: EASE_OUT },
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                        transition: { duration: 0.25, ease: EASE_OUT },
                      }}
                    >
                      <span className="block pt-1.5">{t.caption}</span>
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>
            );
          })}
        </div>
      )}

      {/* Tour pills (below lg): horizontal strip above the frame. */}
      {tour && (
        <div className="relative z-[2] mb-4 flex flex-col items-center gap-3 select-none lg:hidden">
          <div className="flex flex-wrap items-center justify-center gap-2">
            {tabs.map((t) => {
              const active = t.key === activeView;
              return (
                <button
                  key={t.key}
                  type="button"
                  tabIndex={-1}
                  onClick={() => selectView(t.key)}
                  className="relative overflow-hidden rounded-full px-4 py-1.5 text-[13px] font-medium transition-colors duration-300"
                  style={{
                    color: active ? "var(--foreground)" : c.pillText,
                    border: `1px solid ${active ? "color-mix(in srgb, var(--primary) 55%, transparent)" : c.pillBorder}`,
                    background: active
                      ? "color-mix(in srgb, var(--primary) 10%, transparent)"
                      : "transparent",
                  }}
                >
                  {t.label}
                  {active && (
                    <motion.span
                      key={`${activeView}-${rotationEpoch}`}
                      className="absolute inset-x-0 bottom-0 h-[2px] origin-left"
                      style={{ background: "var(--primary)" }}
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{
                        duration: VIEW_ROTATE_MS / 1000,
                        ease: "linear",
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
          {/* Fixed-height slot so caption swaps never shift the frame below. */}
          <div className="flex h-10 w-full items-start justify-center sm:h-6">
            <AnimatePresence mode="wait" initial={false}>
              <motion.p
                key={activeView}
                className="max-w-[46ch] text-center text-[13.5px] leading-snug text-on-dark/65"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.3, ease: EASE_OUT }}
              >
                {tabs.find((t) => t.key === activeView)?.caption}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Window chrome — persistent across the view rotation below; only the
          content inside swaps, so the frame itself never re-animates. */}
      <div
        className={`relative rounded-2xl overflow-hidden border ${glassActive ? "hero-glass-frame" : ""}`}
        style={
          glassActive
            ? undefined
            : {
                background: c.chromeBg,
                borderColor: c.chromeBorder,
                boxShadow: c.shadow,
              }
        }
      >
        <div
          className={
            glassActive
              ? "hero-glass-titlebar shrink-0"
              : "flex items-center gap-1.5 px-4 py-2.5 shrink-0"
          }
          style={
            glassActive
              ? undefined
              : { borderBottom: `1px solid ${c.chromeBorder}` }
          }
        >
          <span
            className={
              glassActive ? "hero-glass-dot" : "w-2.5 h-2.5 rounded-full"
            }
            style={{ background: glassActive ? "#FF5F57" : "#ec6a5e" }}
          />
          <span
            className={
              glassActive ? "hero-glass-dot" : "w-2.5 h-2.5 rounded-full"
            }
            style={{ background: glassActive ? "#FEBC2E" : "#f4bf4f" }}
          />
          <span
            className={
              glassActive ? "hero-glass-dot" : "w-2.5 h-2.5 rounded-full"
            }
            style={{ background: glassActive ? "#28C840" : "#61c454" }}
          />
        </div>

        {mounted ? (
          <div className="relative aspect-[3/4] md:aspect-[2980/1894] overflow-hidden">
            <AnimatePresence initial={false}>
              <motion.div
                key={activeView}
                className="absolute inset-0"
                initial={{ opacity: 0, y: -28, scale: 0.9 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: { duration: 0.6, ease: EASE_OUT, delay: 0.12 },
                }}
                exit={{
                  opacity: 0,
                  y: 48,
                  scale: 0.94,
                  transition: { duration: 0.45, ease: EASE_OUT },
                }}
              >
                {activeViewContent}
              </motion.div>
            </AnimatePresence>

            {tour && (
              <div className="pointer-events-none absolute inset-0 z-[2] hidden md:block">
                <AnimatePresence initial={false}>
                  {(viewCallouts[activeView] ?? []).map((co, i) => (
                    <motion.span
                      key={`${activeView}-${co.text}`}
                      className="absolute flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-[13px] font-semibold"
                      style={{
                        ...co.style,
                        background: "var(--primary)",
                        color: "#0a0a0a",
                        boxShadow:
                          "0 6px 24px -4px color-mix(in srgb, var(--primary) 60%, transparent), 0 2px 8px rgba(0,0,0,0.18)",
                      }}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        transition: {
                          delay: 0.9 + i * 0.25,
                          duration: 0.5,
                          ease: EASE_OUT,
                        },
                      }}
                      exit={{ opacity: 0, transition: { duration: 0.2 } }}
                    >
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ background: "#0a0a0a" }}
                      />
                      {co.text}
                    </motion.span>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        ) : (
          activeViewContent
        )}
      </div>

      {activeView === "chat" && floatingPanels}
    </div>
  );
}
